import React, { useState } from 'react';
import { WizardContext } from '../wizard/wizardProvider';
import { getBasicPageTourSteps } from '../MultiProjectDashboard/Wizards/basic_page_tour';
import { MultiProjectDashContext } from '../MultiProjectDashboard/MultiProjectDashboard';

/**
 * GeneralPage component for Training module.
 * Displays general information or content for training.
 */
interface DashboardPageProps {
    showWizardLinks?: boolean;
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ showWizardLinks = false, setDialogOpen }) => {
    const [selectedHelpPage, setSelectedHelpPage] = useState(showWizardLinks ? "wizard" : "shortcuts");

    function getContent(name: string): JSX.Element {
        switch (name) {
            case "wizard":
                return <Wizards setDialogOpen={setDialogOpen} />;
            case "shortcuts":
                return <Shortcuts />
            case "dashboard":
                return <Dashboard />
            default:
                return <Shortcuts />
        }
    }

    return (
        <div className="flex flex-row w-full pt-4">
            <div className="flex flex-col min-w-[200px] mr-2">
                <ul className="text-center">
                    {showWizardLinks && (
                        <li className={(selectedHelpPage == "wizard" ? "bg-table-subheader " : "bg-table-header ") + "rounded-tl-md p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("wizard")}>Wizards</li>
                    )}
                    <li className={(selectedHelpPage == "shortcuts" ? "bg-table-subheader " : "bg-table-header ") + (showWizardLinks ? "" : "rounded-tl-md ") + "p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("shortcuts")}>Keyboard Shortcuts</li>
                    <li className={(selectedHelpPage == "dashboard" ? "bg-table-subheader " : "bg-table-header ") + "rounded-bl-md p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("dashboard")}>Dashboard</li>
                </ul>
            </div>
            <div className="flex flex-col h-[800px] p-2 overflow-y-auto scrollbar">{getContent(selectedHelpPage)}</div>
        </div>
    );
};

export default DashboardPage;

interface WizardsProps {
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Wizards: React.FC<WizardsProps> = ({ setDialogOpen }) => {
    const wizard = React.useContext(WizardContext);
    const multiProjectDashContext = React.useContext(MultiProjectDashContext);

    function onWizardComplete() {
        multiProjectDashContext.toggleDemoMode(false);
    }

    function startGuidedTour() {
        multiProjectDashContext.toggleDemoMode(true);
        wizard.setWizardSteps(getBasicPageTourSteps());
        wizard.setGuidedTourCompleteFunctionCall(() => () => onWizardComplete());
        wizard.startGuidedTour();
        setDialogOpen?.(false);
    }

    function openDemoMode() {
        multiProjectDashContext.toggleDemoMode(true);
        setDialogOpen?.(false);
    }

    return (
        <div className='flex flex-col'>
            <div className='flex flex-row p-2 mb-2'>
                <div className='flex flex-col mr-4'>
                    <button onClick={startGuidedTour} className="ml-2 button-default">Start Guided Tour</button>
                </div>

                <div className='flex flex-col'>
                    <p>
                        The Guided Tour Wizard simply explains the various sections of the Decisions page and what information is required.
                    </p>
                    <p>
                        It does not create any Decisions for you, it is simply a guide to help you understand how to use the Decisions page.
                    </p>
                    <p>
                        This Tour is for when the page is in view only mode.
                    </p>
                </div>
            </div>
            <div className='flex flex-row p-2 mb-2'>
                <div className='flex flex-col mr-4'>
                    <button onClick={openDemoMode} className="ml-2 button-default">Enter Demo Mode</button>
                </div>

                <div className='flex flex-col'>
                    <p>
                        Demo mode allows you to try out the Decisions page without making any actual changes to the data.
                    </p>
                    <p>
                        The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Decisions.
                    </p>
                </div>
            </div>
        </div>
    );
};

const Shortcuts: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Keyboard Shortcuts</h1>
            <table className="table-auto border-collapse border border-slate-400">
                <thead>
                    <tr>
                        <th className="border border-slate-300 p-2">Shortcut</th>
                        <th className="border border-slate-300 p-2">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="border border-slate-300 p-2">Ctrl + B</td>
                        <td className="border border-slate-300 p-2">Hide / Show Sidebar</td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 p-2">Ctrl + J</td>
                        <td className="border border-slate-300 p-2">Open the 'Jump' menu to navigate quickly</td>
                    </tr>
                </tbody>
            </table>
        </>
    );
}

const Dashboard: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Dashboard</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/general/MultiProject_Dashboard.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        The Dashboard provides an overview of multiple projects, displaying key metrics and statuses at a glance.
                    </p>
                    <br />
                    <p>
                        You can choose which projects to display using the project selector at the top.
                    </p>
                    <br />
                    <p>
                        You can choose which Dashboard elements to display using the view selector at the top.
                    </p>
                    <br />
                    <p>
                        Every element on the Dashboard can be clicked and will display relevant information related to that item 
                        or navigate directly to the page in the relevant project to display the data behind the metric.
                    </p>
                    <br />
                    <p>
                        If you are not displaying all projects, you can click the "View All Projects" button to display all projects.
                    </p>
                </div>
            </div>
        </>
    );
}